import { useEffect, useState, type ReactNode } from 'react'
import { toast } from 'sonner'
import {
  getPlanTier,
  type InvoiceHistoryItem,
  type PlanTier,
  type PlanTierId,
  type SubscriptionResponse,
} from '@super/shared-types'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { PageHeader } from '@/components/layout/PageHeader'
import { Badge } from '@/components/ui/badge'
import { ApiError } from '@/lib/api/base-client'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Check } from 'lucide-react'
import { UpdateCardModal } from '@/components/billing/UpdateCardModal'
import { UsageTile } from '@/components/billing/UsageTile'
import { CardBrandMark } from '@/components/billing/CardBrandMark'
import { cn } from '@/lib/utils'
import { billingClient } from '@/lib/api/billing.client'
import { TIER_GRADIENT_CLASSES } from '@/onboarding/tierColors'

const PLAN_INCLUDED = [
  'Voice agents on your number',
  'PMS + CRM integrations',
  'Trial covers your first 5 days',
]

const STATUS_COPY: Record<string, { label: string; tone: string }> = {
  trialing: {
    label: 'Trialing',
    tone: 'bg-blue-100 text-blue-700 hover:bg-blue-100',
  },
  active: {
    label: 'Active',
    tone: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100',
  },
  past_due: {
    label: 'Past due',
    tone: 'bg-amber-100 text-amber-700 hover:bg-amber-100',
  },
  canceled: {
    label: 'Canceled',
    tone: 'bg-muted text-muted-foreground',
  },
  incomplete: {
    label: 'Incomplete',
    tone: 'bg-muted text-muted-foreground',
  },
}

const INVOICE_STATUS_TONE: Record<string, string> = {
  paid: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100',
  open: 'bg-blue-100 text-blue-700 hover:bg-blue-100',
  void: 'bg-muted text-muted-foreground',
  uncollectible: 'bg-amber-100 text-amber-700 hover:bg-amber-100',
  draft: 'bg-muted text-muted-foreground',
}

const formatDate = (iso: string | null): string | null => {
  if (!iso) return null
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

const formatMoney = (amountCents: number, currency: string): string =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amountCents / 100)

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <h2 className="text-sm font-medium">{children}</h2>
)

export function BillingSettingsPage() {
  const [subscription, setSubscription] = useState<
    SubscriptionResponse | null | undefined
  >(undefined)
  const [invoices, setInvoices] = useState<InvoiceHistoryItem[]>([])
  const [invoicesLoading, setInvoicesLoading] = useState(true)
  const [invoicesError, setInvoicesError] = useState<string | null>(null)
  const [forbidden, setForbidden] = useState(false)
  const [updateCardOpen, setUpdateCardOpen] = useState(false)
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false)
  const [canceling, setCanceling] = useState(false)

  const loadInvoices = () => {
    setInvoicesLoading(true)
    setInvoicesError(null)
    return billingClient
      .getInvoices()
      .then(inv => setInvoices(inv))
      .catch(error => {
        console.error('Failed to load invoices', error)
        setInvoicesError('Could not load invoices.')
      })
      .finally(() => setInvoicesLoading(false))
  }

  useEffect(() => {
    let cancelled = false
    billingClient
      .getSubscription()
      .then(sub => {
        if (!cancelled) setSubscription(sub)
      })
      .catch(error => {
        if (cancelled) return
        if (error instanceof ApiError && error.status === 403) {
          setForbidden(true)
          setSubscription(null)
          return
        }
        console.error('Failed to load subscription', error)
        setSubscription(null)
      })
    loadInvoices().then(() => {
      if (cancelled) return
    })
    return () => {
      cancelled = true
    }
  }, [])

  const handleCancel = async () => {
    setCanceling(true)
    try {
      const updated = await billingClient.cancelSubscription()
      setSubscription(updated)
      toast.success('Cancellation scheduled')
      setConfirmCancelOpen(false)
    } catch (error) {
      console.error('Failed to cancel subscription', error)
      toast.error('Could not cancel. Try again.')
    } finally {
      setCanceling(false)
    }
  }

  return (
    <div className="h-full pt-16">
      <div className="space-y-6 p-6 pt-0 mt-6">
        <PageHeader title="Manage your plan, card, and invoices" />

        {forbidden ? (
          <ForbiddenState />
        ) : subscription === undefined ? (
          <BillingSkeleton />
        ) : subscription === null ? (
          <EmptyState />
        ) : (
          <div className="flex flex-col gap-6 pt-2">
            <PlanSection subscription={subscription} />
            {subscription.stripeSubscriptionId ? <UsageTile /> : null}
            <PaymentMethodSection
              subscription={subscription}
              onOpenUpdate={() => setUpdateCardOpen(true)}
            />
            <InvoicesSection
              invoices={invoices}
              loading={invoicesLoading}
              error={invoicesError}
              onRetry={loadInvoices}
            />
            <CancelSection
              subscription={subscription}
              onOpenCancel={() => setConfirmCancelOpen(true)}
            />
            <ReactivateSection
              subscription={subscription}
              onReactivated={sub => setSubscription(sub)}
              onNeedsCard={() => setUpdateCardOpen(true)}
            />
          </div>
        )}
      </div>

      <UpdateCardModal
        open={updateCardOpen}
        onClose={() => setUpdateCardOpen(false)}
        onCardUpdated={sub => setSubscription(sub)}
      />

      {subscription && (
        <Dialog
          open={confirmCancelOpen}
          onOpenChange={open => !open && setConfirmCancelOpen(false)}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-space-grotesk">
                Cancel subscription?
              </DialogTitle>
              <DialogDescription>
                You'll keep access through{' '}
                {formatDate(subscription.currentPeriodEnd) ??
                  'the end of your period'}
                , then the plan ends. You can resubscribe anytime.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="secondary"
                onClick={() => setConfirmCancelOpen(false)}
                disabled={canceling}
              >
                Keep plan
              </Button>
              <Button
                variant="destructive"
                onClick={handleCancel}
                disabled={canceling}
              >
                {canceling ? 'Canceling…' : 'Cancel subscription'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}

function BillingSkeleton() {
  return (
    <div className="flex flex-col gap-8">
      <div className="bg-secondary/40 h-32 animate-pulse rounded" />
      <div className="bg-secondary/40 h-20 animate-pulse rounded" />
      <div className="bg-secondary/40 h-48 animate-pulse rounded" />
    </div>
  )
}

function EmptyState() {
  return (
    <div className="border-border/60 rounded-md border border-dashed p-10 text-center">
      <p className="font-space-grotesk text-lg font-medium">
        No subscription yet
      </p>
      <p className="text-muted-foreground mt-2 text-sm">
        Finish onboarding to start your trial.
      </p>
    </div>
  )
}

function ForbiddenState() {
  return (
    <div className="border-border/60 rounded-md border border-dashed p-10 text-center">
      <p className="font-space-grotesk text-lg font-medium">
        Admin access required
      </p>
      <p className="text-muted-foreground mt-2 text-sm">
        Ask an admin in your organization to view or manage billing.
      </p>
    </div>
  )
}

function StatusIndicator({ status }: { status: string | null }) {
  const config = status
    ? (STATUS_COPY[status] ?? {
        label: status,
        tone: 'bg-muted text-muted-foreground',
      })
    : null
  if (!config) return null
  return (
    <Badge variant="default" className={cn('text-xs', config.tone)}>
      {config.label}
    </Badge>
  )
}

function BillingCycleCard({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  return (
    <div className="border-border/70 bg-background/60 rounded-md border p-3 space-y-2">
      <p className="text-muted-foreground font-fragment-mono text-[10px] uppercase tracking-[0.14em]">
        {label}
      </p>
      <dl className="space-y-1">{children}</dl>
    </div>
  )
}

function BillingCycleRow({ k, v }: { k: string; v: string | null }) {
  if (!v) return null
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-muted-foreground text-xs">{k}</dt>
      <dd className="font-space-grotesk text-xs font-medium tabular-nums">
        {v}
      </dd>
    </div>
  )
}

function planIncludedFor(tier: PlanTier): string[] {
  const perMinute = formatUsd(tier.perMinuteUsd)
  const perSms = formatUsd(tier.perSmsUsd)
  return [
    `${tier.includedMinutes.toLocaleString()} minutes / cycle · then ${perMinute}/min`,
    `${tier.includedSms.toLocaleString()} SMS / cycle · then ${perSms}/SMS`,
    'PMS + CRM integrations',
    'Trial covers your first 5 days',
  ]
}

function addOneMonth(iso: string): Date {
  const d = new Date(iso)
  return new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate()),
  )
}

/**
 * Stripe's canonical "one month" for proration on `interval=month` plans is
 * a flat 30 days (2_592_000 seconds), regardless of the calendar month the
 * anchor falls on. Using calendar days (28/30/31) would over- or under-count.
 */
const STRIPE_MONTH_MS = 30 * 24 * 60 * 60 * 1000

/**
 * Prorated first-cycle price matching Stripe's `create_prorations` output
 * for a monthly-interval subscription:
 *   monthly × (firstCycleEnd − trialEnd) / STRIPE_MONTH_MS
 */
function proratedFirstCycleUsd(
  tier: PlanTier,
  trialEndsAt: string | null,
  firstCycleEndsAt: string | null,
): number | null {
  if (!trialEndsAt || !firstCycleEndsAt) return null
  const proratedStart = new Date(trialEndsAt).getTime()
  const proratedEnd = new Date(firstCycleEndsAt).getTime()
  if (proratedEnd <= proratedStart) return null
  const usd = (tier.priceUsd * (proratedEnd - proratedStart)) / STRIPE_MONTH_MS
  return Math.round(usd * 100) / 100
}

function formatUsd(amount: number): string {
  return `$${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

function isFuture(iso: string | null): boolean {
  return iso ? new Date(iso).getTime() > Date.now() : false
}

function isPast(iso: string | null): boolean {
  return iso ? new Date(iso).getTime() <= Date.now() : false
}

function PlanSection({ subscription }: { subscription: SubscriptionResponse }) {
  const cancelDate = formatDate(subscription.currentPeriodEnd)
  const tier: PlanTier | null = subscription.planTierId
    ? getPlanTier(subscription.planTierId as PlanTierId)
    : null

  // Free trial box: caller is currently trialing (or trial is still future).
  const showFreeTrial =
    subscription.status === 'trialing' && isFuture(subscription.trialEndsAt)

  // Prorated month box:
  //   upcoming = we're still in trial, prorated starts at trial end
  //   within   = trial ended, we're inside the partial-month window
  //   otherwise hide the box entirely.
  const inProratedWindow =
    isPast(subscription.trialEndsAt) && isFuture(subscription.firstCycleEndsAt)
  const proratedUpcoming =
    isFuture(subscription.trialEndsAt) &&
    isFuture(subscription.firstCycleEndsAt)
  const showProrated = inProratedWindow || proratedUpcoming
  const proratedUsd = tier
    ? proratedFirstCycleUsd(
        tier,
        subscription.trialEndsAt,
        subscription.firstCycleEndsAt,
      )
    : null

  // Upcoming full month box: derive the next full cycle start/end.
  //   If a prorated end is scheduled → next full cycle starts there.
  //   Otherwise → next cycle starts at currentPeriodEnd of the active cycle.
  const upcomingMonthStart =
    subscription.firstCycleEndsAt ?? subscription.currentPeriodEnd
  const upcomingMonthEnd = upcomingMonthStart
    ? addOneMonth(upcomingMonthStart).toISOString()
    : null
  const showUpcomingMonth =
    tier != null && upcomingMonthStart != null && upcomingMonthEnd != null

  return (
    <section className="space-y-3">
      <div className="flex h-6 items-center justify-between">
        <SectionLabel>Current plan</SectionLabel>
        <StatusIndicator status={subscription.status} />
      </div>

      {subscription.cancelAtPeriodEnd && cancelDate && (
        <div className="border-amber-200 bg-amber-50/60 rounded-md border p-2.5 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-200">
          Your plan cancels on {cancelDate}. You keep access until then.
        </div>
      )}

      {subscription.paymentActionRequiredInvoiceId &&
        subscription.paymentActionRequiredUrl && (
          <div className="border-amber-200 bg-amber-50/60 rounded-md border p-2.5 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-200">
            Action needed — your bank needs to confirm the last charge.{' '}
            <a
              href={subscription.paymentActionRequiredUrl}
              target="_blank"
              rel="noreferrer"
              className="font-medium underline underline-offset-2"
            >
              Confirm now
            </a>
            .
          </div>
        )}

      <div
        className={cn(
          'border-border/80 rounded-lg border p-5 space-y-4',
          tier
            ? TIER_GRADIENT_CLASSES[tier.id as PlanTierId]
            : 'bg-gradient-to-br from-zinc-50 via-zinc-50/60 to-transparent dark:from-zinc-900/60 dark:via-zinc-900/40 dark:to-transparent',
        )}
      >
        <div className="flex items-start justify-between gap-8">
          <div className="space-y-2 min-w-0">
            <p className="font-space-grotesk text-xl font-semibold tracking-tight">
              Super — {tier ? `${tier.label} plan` : 'Base plan'}
            </p>
            <ul className="space-y-1">
              {(tier ? planIncludedFor(tier) : PLAN_INCLUDED).map(item => (
                <li
                  key={item}
                  className="text-muted-foreground flex items-start gap-2 text-sm"
                >
                  <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground/70" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          {tier && (
            <div className="text-right shrink-0">
              <p className="font-space-grotesk text-xl font-semibold tracking-tight tabular-nums">
                ${tier.priceUsd.toLocaleString()}
                <span className="text-muted-foreground text-sm font-normal">
                  /mo
                </span>
              </p>
            </div>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          {showFreeTrial && (
            <BillingCycleCard label="Free trial">
              <BillingCycleRow
                k="Ends"
                v={formatDate(subscription.trialEndsAt)}
              />
            </BillingCycleCard>
          )}

          {showProrated && (
            <BillingCycleCard label="Pro-rated month">
              <BillingCycleRow
                k={proratedUpcoming ? 'Starting' : 'Started'}
                v={formatDate(subscription.trialEndsAt)}
              />
              <BillingCycleRow
                k="Ending"
                v={formatDate(subscription.firstCycleEndsAt)}
              />
              {proratedUsd != null && (
                <BillingCycleRow k="Price" v={formatUsd(proratedUsd)} />
              )}
            </BillingCycleCard>
          )}

          {showUpcomingMonth && (
            <BillingCycleCard label="Upcoming month">
              <BillingCycleRow
                k="Starting"
                v={formatDate(upcomingMonthStart)}
              />
              <BillingCycleRow k="Ending" v={formatDate(upcomingMonthEnd)} />
              <BillingCycleRow k="Price" v={formatUsd(tier!.priceUsd)} />
            </BillingCycleCard>
          )}
        </div>
      </div>
    </section>
  )
}

function PaymentMethodSection({
  subscription,
  onOpenUpdate,
}: {
  subscription: SubscriptionResponse
  onOpenUpdate: () => void
}) {
  const { card } = subscription
  return (
    <section className="space-y-3">
      <div className="flex h-6 items-center justify-between">
        <SectionLabel>Payment method</SectionLabel>
        <button
          type="button"
          onClick={onOpenUpdate}
          className="text-foreground hover:text-foreground/80 text-xs underline underline-offset-4"
        >
          Update card
        </button>
      </div>

      {card ? (
        <div className="border-border rounded-lg border p-5 flex items-center justify-between gap-6">
          <div className="flex items-center gap-4 min-w-0">
            <CardBrandMark brand={card.brand} />
            <div className="space-y-1">
              <p className="font-space-grotesk text-base font-medium tabular-nums">
                <span className="text-muted-foreground">•••• </span>
                {card.last4}
              </p>
              <p className="text-muted-foreground text-sm">
                Charges begin after your trial ends.
              </p>
            </div>
          </div>

          <div className="text-right shrink-0">
            <p className="text-muted-foreground text-xs">Expires</p>
            <p className="font-space-grotesk mt-0.5 text-sm font-medium tabular-nums">
              {String(card.expMonth).padStart(2, '0')}/
              {String(card.expYear).slice(-2)}
            </p>
          </div>
        </div>
      ) : (
        <div className="border-border flex items-center justify-center rounded-lg border border-dashed p-5">
          <p className="text-muted-foreground text-sm">No card on file yet.</p>
        </div>
      )}
    </section>
  )
}

function InvoicesSection({
  invoices,
  loading,
  error,
  onRetry,
}: {
  invoices: InvoiceHistoryItem[]
  loading: boolean
  error: string | null
  onRetry: () => void
}) {
  return (
    <section className="space-y-3">
      <SectionLabel>Invoice history</SectionLabel>
      {loading ? (
        <div className="bg-secondary/40 h-16 animate-pulse rounded" />
      ) : error ? (
        <div className="border-amber-200 bg-amber-50/60 flex items-center justify-between rounded-md border p-3 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-200">
          <span>{error}</span>
          <button
            type="button"
            onClick={onRetry}
            className="underline underline-offset-4"
          >
            Try again
          </button>
        </div>
      ) : invoices.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          No invoices yet. Your first invoice will appear after your trial ends.
        </p>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="hidden lg:table-cell">Invoice</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[70px]" align="center">
                  View
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.map(invoice => (
                <TableRow
                  key={invoice.id}
                  className={cn(
                    invoice.hostedInvoiceUrl &&
                      'cursor-pointer hover:bg-muted/50',
                  )}
                  onClick={() => {
                    if (invoice.hostedInvoiceUrl) {
                      window.open(
                        invoice.hostedInvoiceUrl,
                        '_blank',
                        'noreferrer',
                      )
                    }
                  }}
                >
                  <TableCell>
                    <div className="font-medium tabular-nums">
                      {formatDate(invoice.paidAt ?? invoice.createdAt)}
                    </div>
                    <div className="lg:hidden text-muted-foreground font-mono text-xs">
                      {invoice.number ?? '—'}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{invoice.title}</TableCell>
                  <TableCell className="hidden lg:table-cell text-muted-foreground font-mono text-xs">
                    {invoice.number ?? '—'}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {formatMoney(
                      invoice.amountPaid || invoice.amountDue,
                      invoice.currency,
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="default"
                      className={cn(
                        'text-xs capitalize',
                        INVOICE_STATUS_TONE[invoice.status] ??
                          'bg-muted text-muted-foreground',
                      )}
                    >
                      {invoice.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    {invoice.hostedInvoiceUrl && (
                      <a
                        href={invoice.hostedInvoiceUrl}
                        target="_blank"
                        rel="noreferrer"
                        onClick={e => e.stopPropagation()}
                        className="text-xs underline underline-offset-4"
                      >
                        Open
                      </a>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  )
}

function CancelSection({
  subscription,
  onOpenCancel,
}: {
  subscription: SubscriptionResponse
  onOpenCancel: () => void
}) {
  if (subscription.cancelAtPeriodEnd || subscription.status === 'canceled') {
    return null
  }
  return (
    <section className="space-y-3">
      <SectionLabel>Cancel subscription</SectionLabel>
      <div className="border rounded-lg p-4 flex items-center justify-between gap-6">
        <p className="text-muted-foreground text-sm">
          Ends billing at the close of your current period. You keep access
          until then.
        </p>
        <Button size="sm" className="shrink-0" onClick={onOpenCancel}>
          Cancel subscription
        </Button>
      </div>
    </section>
  )
}

function ReactivateSection({
  subscription,
  onReactivated,
  onNeedsCard,
}: {
  subscription: SubscriptionResponse
  onReactivated: (sub: SubscriptionResponse) => void
  onNeedsCard: () => void
}) {
  const [busy, setBusy] = useState(false)
  if (
    subscription.status !== 'canceled' &&
    subscription.status !== 'incomplete_expired'
  ) {
    return null
  }
  const handleReactivate = async () => {
    setBusy(true)
    try {
      const updated = await billingClient.reactivateSubscription()
      onReactivated(updated)
      toast.success('Subscription reactivated')
    } catch (error) {
      if (error instanceof ApiError && error.status === 400) {
        onNeedsCard()
        toast.info('Add a card to reactivate')
      } else {
        console.error('Failed to reactivate subscription', error)
        toast.error('Could not reactivate. Try again.')
      }
    } finally {
      setBusy(false)
    }
  }
  return (
    <section className="space-y-3">
      <SectionLabel>Reactivate subscription</SectionLabel>
      <div className="border rounded-lg p-4 flex items-center justify-between gap-6">
        <p className="text-muted-foreground text-sm">
          Restart your plan with the card on file. Fresh 5-day trial; full
          billing begins on the 1st of next month.
        </p>
        <Button
          size="sm"
          className="shrink-0"
          disabled={busy}
          onClick={handleReactivate}
        >
          {busy ? 'Reactivating…' : 'Reactivate subscription'}
        </Button>
      </div>
    </section>
  )
}
